import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
  initCommonClearCreate,
  initCommonClearDelete,
  initCommonClearEdit,
  initCommonClearList,
  initCommonCreate,
  initCommonCreateResult,
  initCommonDelete,
  initCommonDeleteResult,
  initCommonEdit,
  initCommonEditResult,
  initCommonGetList,
  initCommonSetListResult,
} from "../highOrderReducers/commonReducer";
import {
  ICreatePlanePayload, IDeletePlanePayload, IEditPlanePayload,
  IFleetInitialState,
  IGetPlaneListPayload, ISetCreatePlaneResultPayload, ISetDeletePlaneResultPayload, ISetEditPlaneResultPayload,
  ISetPlaneListResultPayload, ISubmitPlaneForReviewPayload, IGetPlaneApprovalListPayload,
  IDeclinePlanePayload, IConfirmPlanePayload
} from "./fleetReducerTypes";
import {mapPlaneEntityToIPlane} from "./fleetMappers";

const fleetInitialState: IFleetInitialState = {
  itemsObject: {
    items: [],
    isLoading: true,
    totalCount: 0,
  },
  create: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  edit: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  delete: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  submitForReview: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  approvalList: {
    items: [],
    isLoading: false,
    totalCount: 0,
  },
  decline: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  confirm: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  }
}

const fleetSlice = createSlice({
  name: 'fleet',
  initialState: fleetInitialState,
  reducers: {
    getPlaneList(state, {payload}: PayloadAction<IGetPlaneListPayload>) {
      initCommonGetList(state, payload);
    },
    clearPlaneList(state) {
      initCommonClearList(state);
    },
    setPlaneListResult(state, {payload}: PayloadAction<ISetPlaneListResultPayload>) {
      initCommonSetListResult(state, payload, mapPlaneEntityToIPlane);
    },

    createPlane(state, {payload}: PayloadAction<ICreatePlanePayload>) {
      initCommonCreate(state, payload);
    },
    clearCreatePlane(state) {
      initCommonClearCreate(state);
    },
    setCreatePlaneResult(state, {payload}: PayloadAction<ISetCreatePlaneResultPayload>) {
      initCommonCreateResult(state, payload, mapPlaneEntityToIPlane)
    },

    editPlane(state, {payload}: PayloadAction<IEditPlanePayload>) {
      initCommonEdit(state, payload);
    },
    clearEditPlane(state) {
      initCommonClearEdit(state);
    },
    setEditPlaneResult(state, {payload}: PayloadAction<ISetEditPlaneResultPayload>) {
      initCommonEditResult(state, payload, mapPlaneEntityToIPlane);
    },

    deletePlane(state, {payload}: PayloadAction<IDeletePlanePayload>) {
      initCommonDelete(state, payload);
    },
    clearDeletePlane(state) {
      initCommonClearDelete(state);
    },
    setDeletePlaneResult(state, {payload}: PayloadAction<ISetDeletePlaneResultPayload>) {
      initCommonDeleteResult(state, payload);
    },

    submitPlaneForReview(state, {payload}: PayloadAction<ISubmitPlaneForReviewPayload>) {
      state.submitForReview.isLoading = true;
      state.submitForReview.isSuccess = false;
      state.submitForReview.errorMessage = '';
    },
    clearSubmitPlaneForReview(state) {
      state.submitForReview.isLoading = false;
      state.submitForReview.isSuccess = false;
      state.submitForReview.errorMessage = '';
    },
    setSubmitPlaneForReviewResult(state, {payload}: PayloadAction<{isSuccess: boolean; errorMessage?: string}>) {
      state.submitForReview.isLoading = false;
      state.submitForReview.isSuccess = payload.isSuccess;
      state.submitForReview.errorMessage = payload.errorMessage || '';
    },

    getPlaneApprovalList(state, {payload}: PayloadAction<IGetPlaneApprovalListPayload>) {
      state.approvalList.isLoading = true;
      if (payload.page === 0) {
        state.approvalList.items = [];
        state.approvalList.totalCount = 0;
      }
    },
    clearPlaneApprovalList(state) {
      state.approvalList.items = [];
      state.approvalList.totalCount = 0;
      state.approvalList.isLoading = false;
    },
    setPlaneApprovalListResult(state, {payload}: PayloadAction<ISetPlaneListResultPayload>) {
      const mappedList = payload.items.map(item => mapPlaneEntityToIPlane(item));
      state.approvalList.items = mappedList;
      state.approvalList.totalCount = payload.totalCount;
      state.approvalList.isLoading = false;
    },

    declinePlane(state, {payload}: PayloadAction<IDeclinePlanePayload>) {
      state.decline.isLoading = true;
      state.decline.isSuccess = false;
      state.decline.errorMessage = '';
    },
    clearDeclinePlane(state) {
      state.decline.isLoading = false;
      state.decline.isSuccess = false;
      state.decline.errorMessage = '';
    },
    setDeclinePlaneResult(state, {payload}: PayloadAction<{isSuccess: boolean; errorMessage?: string}>) {
      state.decline.isLoading = false;
      state.decline.isSuccess = payload.isSuccess;
      state.decline.errorMessage = payload.errorMessage || '';
    },

    confirmPlane(state, {payload}: PayloadAction<IConfirmPlanePayload>) {
      state.confirm.isLoading = true;
      state.confirm.isSuccess = false;
      state.confirm.errorMessage = '';
    },
    clearConfirmPlane(state) {
      state.confirm.isLoading = false;
      state.confirm.isSuccess = false;
      state.confirm.errorMessage = '';
    },
    setConfirmPlaneResult(state, {payload}: PayloadAction<{isSuccess: boolean; errorMessage?: string}>) {
      state.confirm.isLoading = false;
      state.confirm.isSuccess = payload.isSuccess;
      state.confirm.errorMessage = payload.errorMessage || '';
    },
  },
});

export const {
  getPlaneList,
  clearPlaneList,
  setPlaneListResult,

  createPlane,
  clearCreatePlane,
  setCreatePlaneResult,

  editPlane,
  clearEditPlane,
  setEditPlaneResult,

  deletePlane,
  clearDeletePlane,
  setDeletePlaneResult,

  submitPlaneForReview,
  clearSubmitPlaneForReview,
  setSubmitPlaneForReviewResult,

  getPlaneApprovalList,
  clearPlaneApprovalList,
  setPlaneApprovalListResult,

  declinePlane,
  clearDeclinePlane,
  setDeclinePlaneResult,

  confirmPlane,
  clearConfirmPlane,
  setConfirmPlaneResult,
} = fleetSlice.actions;
export default fleetSlice.reducer;